import { GrammarCategory } from '../../../../types/types';

export const grammaire: GrammarCategory = {

  // ============================================================
  // UNITÉ 1 — PRONOMS & STRUCTURE
  // ============================================================

  'Pronoms sujets': {
    'ngai': {
      translation: "Je / Moi",
      translationEn: "I / Me",
      explanation: "Pronom sujet 1re personne du singulier. En lingala, chaque pronom correspond à un préfixe verbal : ngai → na- (nazali, nakokende).",
      explanationEn: "First-person singular subject pronoun. In Lingala, each pronoun corresponds to a verbal prefix: ngai → na- (nazali, nakokende).",
      example: "Ngai nazali koliya. (Je suis en train de manger.)",
      exampleEn: "Ngai nazali koliya. (I am eating.)",
      audio: "Li-ngai"
    },
    'yo': {
      translation: "Tu / Toi",
      translationEn: "You (singular)",
      explanation: "Pronom sujet 2e personne du singulier. Préfixe verbal : o- (ozali, okokende).",
      explanationEn: "Second-person singular subject pronoun. Verbal prefix: o- (ozali, okokende).",
      example: "Yo ozali koloba. (Tu es en train de parler.)",
      exampleEn: "Yo ozali koloba. (You are speaking.)",
      audio: "Li-yo"
    },
    'ye': {
      translation: "Il / Elle",
      translationEn: "He / She",
      explanation: "Pronom sujet 3e personne du singulier — pas de distinction de genre. Préfixe verbal : a- (azali, akokende).",
      explanationEn: "Third-person singular subject pronoun — no gender distinction. Verbal prefix: a- (azali, akokende).",
      example: "Ye azali kosala. (Il/Elle est en train de travailler.)",
      exampleEn: "Ye azali kosala. (He/She is working.)",
      audio: "Li-ye"
    },
    'biso': {
      translation: "Nous",
      translationEn: "We",
      explanation: "Pronom sujet 1re personne du pluriel. Préfixe verbal : to- (tozali, tokokende).",
      explanationEn: "First-person plural subject pronoun. Verbal prefix: to- (tozali, tokokende).",
      example: "Biso tozali koyekola. (Nous sommes en train d'étudier.)",
      exampleEn: "Biso tozali koyekola. (We are studying.)",
      audio: "Li-biso"
    },
    'bino': {
      translation: "Vous",
      translationEn: "You (plural)",
      explanation: "Pronom sujet 2e personne du pluriel. Préfixe verbal : bo- (bozali, bokokende).",
      explanationEn: "Second-person plural subject pronoun. Verbal prefix: bo- (bozali, bokokende).",
      example: "Bino bozali kolala. (Vous êtes en train de dormir.)",
      exampleEn: "Bino bozali kolala. (You are sleeping.)",
      audio: "Li-bino"
    },
    'bango': {
      translation: "Ils / Elles",
      translationEn: "They",
      explanation: "Pronom sujet 3e personne du pluriel — pas de distinction de genre. Préfixe verbal : ba- (bazali, bakokende).",
      explanationEn: "Third-person plural subject pronoun — no gender distinction. Verbal prefix: ba- (bazali, bakokende).",
      example: "Bango bazali kosana. (Ils/Elles sont en train de jouer.)",
      exampleEn: "Bango bazali kosana. (They are playing.)",
      audio: "Li-bango"
    },
  },

  'Pronoms objets': {
    'ngai (objet)': {
      translation: "Me / Moi",
      translationEn: "Me",
      explanation: "Pronom objet 1re personne du singulier. En lingala, le pronom objet se place après le verbe, en fin de proposition.",
      explanationEn: "First-person singular object pronoun. In Lingala, the object pronoun is placed after the verb, at the end of the clause.",
      example: "Abandaki ngai. (Il m'a abandonné.)",
      exampleEn: "Abandaki ngai. (He abandoned me.)",
      audio: "Li-ngai"
    },
    'yo (objet)': {
      translation: "Te / Toi",
      translationEn: "You",
      explanation: "Pronom objet 2e personne du singulier.",
      explanationEn: "Second-person singular object pronoun.",
      example: "Nalobeli yo. (Je t'ai parlé.)",
      exampleEn: "Nalobeli yo. (I spoke to you.)",
      audio: "Li-yo"
    },
    'ye (objet)': {
      translation: "Le / La / Lui",
      translationEn: "Him / Her / It",
      explanation: "Pronom objet 3e personne du singulier — neutre en genre.",
      explanationEn: "Third-person singular object pronoun — gender-neutral.",
      example: "Nabonaki ye. (Je l'ai vu/e.)",
      exampleEn: "Nabonaki ye. (I saw him/her.)",
      audio: "Li-ye"
    },
    'biso (objet)': {
      translation: "Nous",
      translationEn: "Us",
      explanation: "Pronom objet 1re personne du pluriel.",
      explanationEn: "First-person plural object pronoun.",
      example: "Asaleli biso. (Il/Elle a travaillé pour nous.)",
      exampleEn: "Asaleli biso. (He/She worked for us.)",
      audio: "Li-biso"
    },
    'bino (objet)': {
      translation: "Vous",
      translationEn: "You (plural)",
      explanation: "Pronom objet 2e personne du pluriel.",
      explanationEn: "Second-person plural object pronoun.",
      example: "Nalobeli bino. (Je vous ai parlé.)",
      exampleEn: "Nalobeli bino. (I spoke to you.)",
      audio: "Li-bino"
    },
    'bango (objet)': {
      translation: "Les / Eux / Elles",
      translationEn: "Them",
      explanation: "Pronom objet 3e personne du pluriel.",
      explanationEn: "Third-person plural object pronoun.",
      example: "Nabonaki bango. (Je les ai vus/es.)",
      exampleEn: "Nabonaki bango. (I saw them.)",
      audio: "Li-bango"
    },
  },

  'Pronoms possessifs': {
    'ya ngai': {
      translation: "Mon / Ma / Mes",
      translationEn: "My",
      explanation: "Possessif 1re pers. sg. En lingala, «ya» + pronom = possessif, toujours placé APRÈS le nom (contrairement au français).",
      explanationEn: "1st person sg. possessive. In Lingala, «ya» + pronoun = possessive, always placed AFTER the noun (unlike in French).",
      example: "Ndako ya ngai ezali monene. (Ma maison est grande.)",
      exampleEn: "Ndako ya ngai ezali monene. (My house is big.)"
    },
    'ya yo': {
      translation: "Ton / Ta / Tes",
      translationEn: "Your",
      explanation: "Possessif 2e pers. sg.",
      explanationEn: "2nd person sg. possessive.",
      example: "Kitabu ya yo ezali awa. (Ton livre est ici.)",
      exampleEn: "Kitabu ya yo ezali awa. (Your book is here.)"
    },
    'ya ye': {
      translation: "Son / Sa / Ses",
      translationEn: "His / Her",
      explanation: "Possessif 3e pers. sg. — neutre en genre. Pas de distinction son/sa.",
      explanationEn: "3rd person sg. possessive — gender-neutral. No distinction between his/her.",
      example: "Mwana ya ye azali kitoko. (Son enfant est beau.)",
      exampleEn: "Mwana ya ye azali kitoko. (His/Her child is beautiful.)"
    },
    'ya biso': {
      translation: "Notre / Nos",
      translationEn: "Our",
      explanation: "Possessif 1re pers. pl.",
      explanationEn: "1st person pl. possessive.",
      example: "Nzela ya biso. (Notre chemin.)",
      exampleEn: "Nzela ya biso. (Our path.)"
    },
    'ya bino': {
      translation: "Votre / Vos",
      translationEn: "Your (plural)",
      explanation: "Possessif 2e pers. pl.",
      explanationEn: "2nd person pl. possessive.",
      example: "Ndako ya bino ezali wapi? (Où est votre maison?)",
      exampleEn: "Ndako ya bino ezali wapi? (Where is your house?)"
    },
    'ya bango': {
      translation: "Leur / Leurs",
      translationEn: "Their",
      explanation: "Possessif 3e pers. pl.",
      explanationEn: "3rd person pl. possessive.",
      example: "Mwana ya bango azali awa. (Leur enfant est ici.)",
      exampleEn: "Mwana ya bango azali awa. (Their child is here.)"
    },
  },

  'Pronoms démonstratifs': {
    'oyo': {
      translation: "Ce / Cet / Cette / Celui-ci",
      translationEn: "This / This one",
      explanation: "Démonstratif de proximité (singulier). Très courant en lingala — il se place après le nom qu'il qualifie.",
      explanationEn: "Demonstrative of proximity (singular). Very common in Lingala — it is placed after the noun it qualifies.",
      example: "Moto oyo azali malamu. (Cette personne est bonne.)",
      exampleEn: "Moto oyo azali malamu. (This person is good.)"
    },
    'wana': {
      translation: "Ce...là / Celui-là",
      translationEn: "That / That one",
      explanation: "Démonstratif d'éloignement (singulier). Désigne quelque chose de plus lointain dans l'espace ou le discours.",
      explanationEn: "Demonstrative of distance (singular). Refers to something farther away in space or in discourse.",
      example: "Ndako wana ezali monene. (Cette maison-là est grande.)",
      exampleEn: "Ndako wana ezali monene. (That house is big.)"
    },
    'baoyo': {
      translation: "Ceux-ci / Celles-ci",
      translationEn: "These (ones)",
      explanation: "Démonstratif de proximité au pluriel pour les personnes : ba- (préfixe pluriel personnes) + oyo.",
      explanationEn: "Plural demonstrative of proximity for people: ba- (plural prefix for people) + oyo.",
      example: "Bato baoyo bazali malamu. (Ces personnes-ci sont bonnes.)",
      exampleEn: "Bato baoyo bazali malamu. (These people are good.)"
    },
    'bawana': {
      translation: "Ceux-là / Celles-là",
      translationEn: "Those (ones)",
      explanation: "Démonstratif d'éloignement au pluriel pour les personnes.",
      explanationEn: "Plural demonstrative of distance for people.",
      example: "Bato bawana bazali wapi? (Où sont ces gens-là?)",
      exampleEn: "Bato bawana bazali wapi? (Where are those people?)"
    },
    'oyo moko': {
      translation: "Ce même / Précisément celui-ci",
      translationEn: "This very one / Precisely this one",
      explanation: "Démonstratif renforcé. «Moko» ajoute une insistance : c'est exactement celui-ci et personne d'autre.",
      explanationEn: "Reinforced demonstrative. «Moko» adds emphasis: it is exactly this one and no other.",
      example: "Moto oyo moko azalaki awa. (C'est précisément cette personne qui était ici.)",
      exampleEn: "Moto oyo moko azalaki awa. (It is precisely this person who was here.)"
    },
    'biloko oyo': {
      translation: "Ces choses / Ces objets",
      translationEn: "These things / These objects",
      explanation: "«Biloko» = choses, objets. Le préfixe «bi-» est le pluriel des choses dans les classes nominales du lingala.",
      explanationEn: "«Biloko» = things, objects. The prefix «bi-» is the plural for things in the Lingala noun classes.",
      example: "Biloko oyo ezali ya bino. (Ces choses vous appartiennent.)",
      exampleEn: "Biloko oyo ezali ya bino. (These things belong to you.)"
    },
  },

  'Pronoms interrogatifs': {
    'nani': {
      translation: "Qui",
      translationEn: "Who",
      explanation: "Pronom interrogatif pour les personnes. Peut se placer en début ou en fin de phrase.",
      explanationEn: "Interrogative pronoun for people. Can be placed at the beginning or end of the sentence.",
      example: "Nani azali awa? (Qui est ici?)",
      exampleEn: "Nani azali awa? (Who is here?)",
      audio: "Li-nani"
    },
    'nini': {
      translation: "Quoi / Que / Quel",
      translationEn: "What / Which",
      explanation: "Pronom interrogatif pour les choses et les actions.",
      explanationEn: "Interrogative pronoun for things and actions.",
      example: "Ozali kosala nini? (Qu'est-ce que tu es en train de faire?)",
      exampleEn: "Ozali kosala nini? (What are you doing?)",
      audio: "Li-nini"
    },
    'wapi': {
      translation: "Où",
      translationEn: "Where",
      explanation: "Interrogatif de lieu. Se place en fin de phrase la plupart du temps.",
      explanationEn: "Interrogative of place. Usually placed at the end of the sentence.",
      example: "Ozali wapi? (Où es-tu?)",
      exampleEn: "Ozali wapi? (Where are you?)",
      audio: "Li-wapi"
    },
    'tango nini': {
      translation: "Quand",
      translationEn: "When",
      explanation: "Interrogatif de temps. «Tango» = moment, temps.",
      explanationEn: "Interrogative of time. «Tango» = moment, time.",
      example: "Okokende tango nini? (Quand est-ce que tu iras?)",
      exampleEn: "Okokende tango nini? (When will you go?)",
      audio: "Li-Tango_nini"
    },
    'ndenge nini': {
      translation: "Comment",
      translationEn: "How",
      explanation: "Interrogatif de manière. «Ndenge» = façon, manière. Aussi utilisé pour demander des nouvelles.",
      explanationEn: "Interrogative of manner. «Ndenge» = way, manner. Also used to ask how someone is doing.",
      example: "Ozali ndenge nini? (Comment vas-tu?)",
      exampleEn: "Ozali ndenge nini? (How are you?)",
      audio: "Li-Ndenge_nini"
    },
    'mpo nini': {
      translation: "Pourquoi",
      translationEn: "Why",
      explanation: "Interrogatif de cause. «Mpo» = pour, à cause de.",
      explanationEn: "Interrogative of cause. «Mpo» = for, because of.",
      example: "Mpo nini ozali kolela? (Pourquoi es-tu en train de pleurer?)",
      exampleEn: "Mpo nini ozali kolela? (Why are you crying?)",
      audio: "Li-Mpo_nini"
    },
    'boni': {
      translation: "Combien",
      translationEn: "How much / How many",
      explanation: "Interrogatif de quantité ou de prix. Très utilisé dans les marchés et commerces.",
      explanationEn: "Interrogative of quantity or price. Widely used in markets and shops.",
      example: "Ezali boni? (C'est combien?)",
      exampleEn: "Ezali boni? (How much is it?)",
      audio: "Li-boni"
    },
  },

  'Structure de la phrase': {
    'Ordre SVO': {
      translation: "Sujet – Verbe – Objet",
      translationEn: "Subject – Verb – Object",
      explanation: "En lingala, l'ordre de base est SVO — comme en français ! Le sujet précède toujours le verbe qui porte le préfixe personnel.",
      explanationEn: "In Lingala, the basic order is SVO — just like in French! The subject always precedes the verb, which carries the personal prefix.",
      example: "Ngai naliyaki bilei. (J'ai mangé de la nourriture.) → Ngai [S] + naliyaki [V] + bilei [O]",
      exampleEn: "Ngai naliyaki bilei. (I ate food.) → Ngai [S] + naliyaki [V] + bilei [O]"
    },
    'Copule zali': {
      translation: "Être (copule)",
      translationEn: "To be (copula)",
      explanation: "Le verbe «zali» est la copule universelle du lingala. Conjugué avec les préfixes : na-, o-, a-, to-, bo-, ba-.",
      explanationEn: "The verb «zali» is the universal copula in Lingala. Conjugated with the prefixes: na-, o-, a-, to-, bo-, ba-.",
      example: "Ye azali mokambi. (Il/Elle est chef.) — a- (préfixe 3sg) + zali",
      exampleEn: "Ye azali mokambi. (He/She is the boss.) — a- (3sg prefix) + zali"
    },
    'Possession avec ya': {
      translation: "Possession (de / à)",
      translationEn: "Possession (of / belonging to)",
      explanation: "La possession s'exprime avec «ya» placé entre le possédé et le possesseur : nom + ya + possesseur. L'inverse du français !",
      explanationEn: "Possession is expressed with «ya» placed between the thing possessed and the possessor: noun + ya + possessor. The reverse order of French!",
      example: "Kitabu ya mwana ezali awa. (Le livre de l'enfant est ici.)",
      exampleEn: "Kitabu ya mwana ezali awa. (The child's book is here.)"
    },
    'Négation avec te': {
      translation: "Négation (ne...pas)",
      translationEn: "Negation (not)",
      explanation: "En lingala, la négation se forme en ajoutant «te» à la fin de la phrase. Pas de «ne» en début comme en français.",
      explanationEn: "In Lingala, negation is formed by adding «te» at the end of the sentence. There is no «ne» at the start, unlike in French.",
      example: "Nakendaki te. (Je ne suis pas allé.) — Comparer : Nakendaki. (Je suis allé.)",
      exampleEn: "Nakendaki te. (I did not go.) — Compare: Nakendaki. (I went.)"
    },
    'Question par intonation': {
      translation: "Question (oui/non)",
      translationEn: "Question (yes/no)",
      explanation: "Les questions fermées se forment uniquement par la montée de l'intonation — la structure reste identique à une affirmation.",
      explanationEn: "Closed questions are formed solely by a rising intonation — the structure stays identical to a statement.",
      example: "Ozali malamu? (Est-ce que tu vas bien?) — même structure que : Ozali malamu. (Tu vas bien.)",
      exampleEn: "Ozali malamu? (Are you well?) — same structure as: Ozali malamu. (You are well.)"
    },
    'Relative avec oyo': {
      translation: "Proposition relative",
      translationEn: "Relative clause",
      explanation: "Le démonstratif «oyo» sert aussi à former les relatives, en suivant directement le nom qu'il qualifie.",
      explanationEn: "The demonstrative «oyo» is also used to form relative clauses, directly following the noun it qualifies.",
      example: "Moto oyo nazalaki elongo na ye azali malamu. (La personne avec qui j'étais est bien.)",
      exampleEn: "Moto oyo nazalaki elongo na ye azali malamu. (The person I was with is good.)"
    },
  },

  // ============================================================
  // UNITÉ 2 — TEMPS VERBAUX & NÉGATION
  // ============================================================

  'Temps verbaux — Présent progressif': {
    'nazali koliya': {
      translation: "Je suis en train de manger",
      translationEn: "I am eating",
      explanation: "Présent progressif, 1re pers. sg. Structure : préfixe + zali + ko- + verbe. Exprime une action en cours au moment où l'on parle.",
      explanationEn: "Present progressive, 1st person sg. Structure: prefix + zali + ko- + verb. Expresses an action in progress at the moment of speaking.",
      example: "Nazali koliya sima ya mosala. (Je suis en train de manger après le travail.)",
      exampleEn: "Nazali koliya sima ya mosala. (I am eating after work.)",
      audio: "Li-Nazali_koliya"
    },
    'ozali koloba': {
      translation: "Tu es en train de parler",
      translationEn: "You are speaking",
      explanation: "Présent progressif, 2e pers. sg. o- + zali + ko- + loba.",
      explanationEn: "Present progressive, 2nd person sg. o- + zali + ko- + loba.",
      example: "Ozali koloba na nani? (Avec qui es-tu en train de parler?)",
      exampleEn: "Ozali koloba na nani? (Who are you talking to?)",
      audio: "Li-Ozali_koloba"
    },
    'azali kosala': {
      translation: "Il/Elle est en train de travailler",
      translationEn: "He/She is working",
      explanation: "Présent progressif, 3e pers. sg. a- + zali + ko- + sala.",
      explanationEn: "Present progressive, 3rd person sg. a- + zali + ko- + sala.",
      example: "Azali kosala na bureau. (Il/Elle est en train de travailler au bureau.)",
      exampleEn: "Azali kosala na bureau. (He/She is working at the office.)",
      audio: "Li-Azali_kosala"
    },
    'tozali koyekola': {
      translation: "Nous sommes en train d'étudier",
      translationEn: "We are studying",
      explanation: "Présent progressif, 1re pers. pl. to- + zali + ko- + yekola.",
      explanationEn: "Present progressive, 1st person pl. to- + zali + ko- + yekola.",
      example: "Tozali koyekola lingala. (Nous sommes en train d'étudier le lingala.)",
      exampleEn: "Tozali koyekola lingala. (We are studying Lingala.)",
      audio: "Li-Tozali_koyekola"
    },
    'bozali kolala': {
      translation: "Vous êtes en train de dormir",
      translationEn: "You are sleeping",
      explanation: "Présent progressif, 2e pers. pl. bo- + zali + ko- + lala.",
      explanationEn: "Present progressive, 2nd person pl. bo- + zali + ko- + lala.",
      example: "Bozali kolala sikoyo? (Vous êtes en train de dormir maintenant?)",
      exampleEn: "Bozali kolala sikoyo? (Are you sleeping right now?)",
      audio: "Li-Bozali_kolala"
    },
    'bazali kosana': {
      translation: "Ils/Elles sont en train de jouer",
      translationEn: "They are playing",
      explanation: "Présent progressif, 3e pers. pl. ba- + zali + ko- + sana.",
      explanationEn: "Present progressive, 3rd person pl. ba- + zali + ko- + sana.",
      example: "Bana bazali kosana na libanda. (Les enfants sont en train de jouer dehors.)",
      exampleEn: "Bana bazali kosana na libanda. (The children are playing outside.)",
      audio: "Li-Bazali_kosana"
    },
  },

  'Temps verbaux — Présent habituel': {
    'naliyaka': {
      translation: "Je mange (habituellement)",
      translationEn: "I eat (habitually)",
      explanation: "Présent habituel, 1re pers. sg. Se forme avec le préfixe + verbe + -aka. Exprime une action répétée ou une habitude régulière.",
      explanationEn: "Habitual present, 1st person sg. Formed with prefix + verb + -aka. Expresses a repeated action or a regular habit.",
      example: "Naliyaka tango nyonso na ntongo. (Je mange toujours le matin.)",
      exampleEn: "Naliyaka tango nyonso na ntongo. (I always eat in the morning.)"
    },
    'olobaka': {
      translation: "Tu parles (habituellement)",
      translationEn: "You speak (habitually)",
      explanation: "Présent habituel, 2e pers. sg. o- + loba + -aka.",
      explanationEn: "Habitual present, 2nd person sg. o- + loba + -aka.",
      example: "Olobaka lingala malamu. (Tu parles bien le lingala habituellement.)",
      exampleEn: "Olobaka lingala malamu. (You speak Lingala well, as a habit.)",
      audio: "Li-olobaka"
    },
    'asalaka': {
      translation: "Il/Elle travaille (habituellement)",
      translationEn: "He/She works (habitually)",
      explanation: "Présent habituel, 3e pers. sg. a- + sala + -aka.",
      explanationEn: "Habitual present, 3rd person sg. a- + sala + -aka.",
      example: "Asalaka kila mokolo. (Il/Elle travaille chaque jour.)",
      exampleEn: "Asalaka kila mokolo. (He/She works every day.)",
      audio: "Li-asalaka"
    },
    'toyekolaka': {
      translation: "Nous étudions (habituellement)",
      translationEn: "We study (habitually)",
      explanation: "Présent habituel, 1re pers. pl. to- + yekola + -aka.",
      explanationEn: "Habitual present, 1st person pl. to- + yekola + -aka.",
      example: "Toyekolaka na soir. (Nous étudions habituellement le soir.)",
      exampleEn: "Toyekolaka na soir. (We usually study in the evening.)",
      audio: "Li-toyekolaka"
    },
    'bolalaka': {
      translation: "Vous dormez (habituellement)",
      translationEn: "You sleep (habitually)",
      explanation: "Présent habituel, 2e pers. pl. bo- + lala + -aka.",
      explanationEn: "Habitual present, 2nd person pl. bo- + lala + -aka.",
      example: "Bolalaka ntango nini? (Quand avez-vous l'habitude de dormir?)",
      exampleEn: "Bolalaka ntango nini? (When do you usually sleep?)",
      audio: "Li-bolalaka"
    },
    'basanaka': {
      translation: "Ils/Elles jouent (habituellement)",
      translationEn: "They play (habitually)",
      explanation: "Présent habituel, 3e pers. pl. ba- + sana + -aka.",
      explanationEn: "Habitual present, 3rd person pl. ba- + sana + -aka.",
      example: "Bana basanaka nsima ya kelasi. (Les enfants jouent habituellement après l'école.)",
      exampleEn: "Bana basanaka nsima ya kelasi. (The children usually play after school.)",
      audio: "Li-basanaka"
    },
    'nazalaka na': {
      translation: "J'ai habituellement / Je possède généralement",
      translationEn: "I usually have / I generally own",
      explanation: "«Zali na» = avoir. Au présent habituel, exprime une possession ou un état récurrents.",
      explanationEn: "«Zali na» = to have. In the habitual present, it expresses a recurring possession or state.",
      example: "Nazalaka na kondima. (J'ai habituellement de la patience.)",
      exampleEn: "Nazalaka na kondima. (I usually have patience.)",
      audio: "Li-Nazalaka_na"
    },
  },

  'Temps verbaux — Passé': {
    'nakendaki': {
      translation: "Je suis allé(e)",
      translationEn: "I went",
      explanation: "Passé, 1re pers. sg. Se forme avec le préfixe + verbe + -aki. S'applique à tout verbe d'action.",
      explanationEn: "Past tense, 1st person sg. Formed with prefix + verb + -aki. Applies to any action verb.",
      example: "Nakendaki na mboka lobi. (Je suis allé au village hier.)",
      exampleEn: "Nakendaki na mboka lobi. (I went to the village yesterday.)"
    },
    'naliyaki': {
      translation: "J'ai mangé",
      translationEn: "I ate",
      explanation: "Passé, 1re pers. sg. na- + liya + -aki.",
      explanationEn: "Past tense, 1st person sg. na- + liya + -aki.",
      example: "Naliyaki bilei ya malamu. (J'ai mangé de la bonne nourriture.)",
      exampleEn: "Naliyaki bilei ya malamu. (I ate good food.)"
    },
    'olobaki': {
      translation: "Tu as parlé",
      translationEn: "You spoke",
      explanation: "Passé, 2e pers. sg. o- + loba + -aki.",
      explanationEn: "Past tense, 2nd person sg. o- + loba + -aki.",
      example: "Olobaki na ye sima ya kelasi. (Tu lui as parlé après l'école.)",
      exampleEn: "Olobaki na ye sima ya kelasi. (You spoke to him/her after school.)"
    },
    'asalaki': {
      translation: "Il/Elle a travaillé",
      translationEn: "He/She worked",
      explanation: "Passé, 3e pers. sg. a- + sala + -aki.",
      explanationEn: "Past tense, 3rd person sg. a- + sala + -aki.",
      example: "Asalaki na mosala monene. (Il/Elle a accompli un grand travail.)",
      exampleEn: "Asalaki na mosala monene. (He/She accomplished a great work.)"
    },
    'tozalaki': {
      translation: "Nous étions",
      translationEn: "We were",
      explanation: "Passé du verbe «zali», 1re pers. pl. to- + zala + -aki → tozalaki.",
      explanationEn: "Past tense of the verb «zali», 1st person pl. to- + zala + -aki → tozalaki.",
      example: "Tozalaki elongo na esengo. (Nous étions ensemble dans la joie.)",
      exampleEn: "Tozalaki elongo na esengo. (We were together in joy.)",
      audio: "Li-tozalaki"
    },
    'bazalaki': {
      translation: "Ils/Elles étaient",
      translationEn: "They were",
      explanation: "Passé du verbe «zali», 3e pers. pl. ba- + zala + -aki → bazalaki.",
      explanationEn: "Past tense of the verb «zali», 3rd person pl. ba- + zala + -aki → bazalaki.",
      example: "Bazalaki na bokebi. (Ils/Elles étaient attentifs.)",
      exampleEn: "Bazalaki na bokebi. (They were attentive.)",
      audio: "Li-bazalaki"
    },
    'nabonaki': {
      translation: "J'ai vu",
      translationEn: "I saw",
      explanation: "Passé du verbe «kobona» (voir), 1re pers. sg. na- + bona + -aki.",
      explanationEn: "Past tense of the verb «kobona» (to see), 1st person sg. na- + bona + -aki.",
      example: "Nabonaki ye na nzela. (Je l'ai vu/e sur le chemin.)",
      exampleEn: "Nabonaki ye na nzela. (I saw him/her on the road.)"
    },
    'nalobaki': {
      translation: "J'ai dit / J'ai parlé",
      translationEn: "I said / I spoke",
      explanation: "Passé du verbe «koloba» (dire/parler), 1re pers. sg. na- + loba + -aki.",
      explanationEn: "Past tense of the verb «koloba» (to say/speak), 1st person sg. na- + loba + -aki.",
      example: "Nalobaki solo. (J'ai dit la vérité.)",
      exampleEn: "Nalobaki solo. (I told the truth.)"
    },
  },

  'Temps verbaux — Futur': {
    'nakokende': {
      translation: "J'irai",
      translationEn: "I will go",
      explanation: "Futur, 1re pers. sg. Se forme avec préfixe + ko- + verbe. «Ko-» marque à la fois le futur et l'infinitif en lingala.",
      explanationEn: "Future tense, 1st person sg. Formed with prefix + ko- + verb. «Ko-» marks both the future and the infinitive in Lingala.",
      example: "Nakokende na mboka lobi. (J'irai au village demain.)",
      exampleEn: "Nakokende na mboka lobi. (I will go to the village tomorrow.)"
    },
    'okokende': {
      translation: "Tu iras",
      translationEn: "You will go",
      explanation: "Futur, 2e pers. sg. o- + ko- + kende.",
      explanationEn: "Future tense, 2nd person sg. o- + ko- + kende.",
      example: "Okokende na kelasi? (Est-ce que tu iras à l'école?)",
      exampleEn: "Okokende na kelasi? (Will you go to school?)"
    },
    'akokende': {
      translation: "Il/Elle ira",
      translationEn: "He/She will go",
      explanation: "Futur, 3e pers. sg. a- + ko- + kende.",
      explanationEn: "Future tense, 3rd person sg. a- + ko- + kende.",
      example: "Akokende na lopital. (Il/Elle ira à l'hôpital.)",
      exampleEn: "Akokende na lopital. (He/She will go to the hospital.)"
    },
    'tokokende': {
      translation: "Nous irons",
      translationEn: "We will go",
      explanation: "Futur, 1re pers. pl. to- + ko- + kende.",
      explanationEn: "Future tense, 1st person pl. to- + ko- + kende.",
      example: "Tokokende elongo. (Nous irons ensemble.)",
      exampleEn: "Tokokende elongo. (We will go together.)"
    },
    'bokokende': {
      translation: "Vous irez",
      translationEn: "You will go (plural)",
      explanation: "Futur, 2e pers. pl. bo- + ko- + kende.",
      explanationEn: "Future tense, 2nd person pl. bo- + ko- + kende.",
      example: "Bokokende tango nini? (Quand est-ce que vous irez?)",
      exampleEn: "Bokokende tango nini? (When will you go?)"
    },
    'bakokende': {
      translation: "Ils/Elles iront",
      translationEn: "They will go",
      explanation: "Futur, 3e pers. pl. ba- + ko- + kende.",
      explanationEn: "Future tense, 3rd person pl. ba- + ko- + kende.",
      example: "Bakokende na ndako ya bango. (Ils/Elles iront chez eux.)",
      exampleEn: "Bakokende na ndako ya bango. (They will go to their home.)"
    },
    'nakoliya': {
      translation: "Je mangerai",
      translationEn: "I will eat",
      explanation: "Futur du verbe «koliya» (manger). na- + ko- + liya. La structure est identique pour tous les verbes.",
      explanationEn: "Future tense of the verb «koliya» (to eat). na- + ko- + liya. The structure is identical for all verbs.",
      example: "Nakoliya nsima ya mosala. (Je mangerai après le travail.)",
      exampleEn: "Nakoliya nsima ya mosala. (I will eat after work.)"
    },
  },

  'Temps verbaux — Impératif': {
    'kende!': {
      translation: "Va! / Pars!",
      translationEn: "Go!",
      explanation: "Impératif singulier de «kokende» (aller). On retire le préfixe et «ko-» : ko-kende → kende!",
      explanationEn: "Singular imperative of «kokende» (to go). The prefix and «ko-» are removed: ko-kende → kende!",
      example: "Kende na ndako! (Va à la maison!)",
      exampleEn: "Kende na ndako! (Go home!)"
    },
    'bokende!': {
      translation: "Allez! (pluriel)",
      translationEn: "Go! (plural)",
      explanation: "Impératif pluriel : on ajoute le préfixe «bo-» devant le verbe : bo + kende = bokende!",
      explanationEn: "Plural imperative: the prefix «bo-» is added before the verb: bo + kende = bokende!",
      example: "Bokende sikoyo! (Allez-y maintenant!)",
      exampleEn: "Bokende sikoyo! (Go now!)"
    },
    'liya!': {
      translation: "Mange!",
      translationEn: "Eat!",
      explanation: "Impératif singulier de «koliya» (manger). ko-liya → liya!",
      explanationEn: "Singular imperative of «koliya» (to eat). ko-liya → liya!",
      example: "Liya bilei na yo! (Mange ta nourriture!)",
      exampleEn: "Liya bilei na yo! (Eat your food!)"
    },
    'sala!': {
      translation: "Travaille!",
      translationEn: "Work!",
      explanation: "Impératif singulier de «kosala» (travailler). ko-sala → sala!",
      explanationEn: "Singular imperative of «kosala» (to work). ko-sala → sala!",
      example: "Sala malamu! (Travaille bien!)",
      exampleEn: "Sala malamu! (Work well!)"
    },
    'yekola!': {
      translation: "Étudie! / Apprends!",
      translationEn: "Study! / Learn!",
      explanation: "Impératif singulier de «koyekola» (étudier/apprendre). ko-yekola → yekola!",
      explanationEn: "Singular imperative of «koyekola» (to study/learn). ko-yekola → yekola!",
      example: "Yekola lingala! (Apprends le lingala!)",
      exampleEn: "Yekola lingala! (Learn Lingala!)"
    },
    'yaka!': {
      translation: "Viens!",
      translationEn: "Come!",
      explanation: "Impératif singulier de «koya» (venir). «Yaka» est la forme renforcée, très courante dans la langue parlée.",
      explanationEn: "Singular imperative of «koya» (to come). «Yaka» is the emphatic form, very common in spoken language.",
      example: "Yaka awa sikoyo! (Viens ici maintenant!)",
      exampleEn: "Yaka awa sikoyo! (Come here now!)"
    },
    'koloba te!': {
      translation: "Ne parle pas! / Tais-toi!",
      translationEn: "Don't speak! / Be quiet!",
      explanation: "Impératif négatif : ko- + verbe + te. À l'impératif négatif, on conserve «ko-» et on ajoute «te» à la fin.",
      explanationEn: "Negative imperative: ko- + verb + te. In the negative imperative, «ko-» is kept and «te» is added at the end.",
      example: "Koloba te! (Ne parle pas!)",
      exampleEn: "Koloba te! (Don't speak!)"
    },
  },

  'Négation': {
    'Nazali koliya te': {
      translation: "Je ne suis pas en train de manger",
      translationEn: "I am not eating",
      explanation: "Négation au présent progressif. «Te» se place toujours en fin de phrase, après tous les autres éléments.",
      explanationEn: "Negation in the present progressive. «Te» is always placed at the end of the sentence, after all other elements.",
      example: "Nazali koliya te sikoyo. (Je ne suis pas en train de manger maintenant.)",
      exampleEn: "Nazali koliya te sikoyo. (I am not eating right now.)"
    },
    'Naliyaka te': {
      translation: "Je ne mange pas (habituellement)",
      translationEn: "I do not eat (habitually)",
      explanation: "Négation au présent habituel. «Te» annule l'habitude ou la généralité.",
      explanationEn: "Negation in the habitual present. «Te» cancels out the habit or generalization.",
      example: "Naliyaka nyama te. (Je ne mange pas de viande habituellement.)",
      exampleEn: "Naliyaka nyama te. (I do not usually eat meat.)"
    },
    'Nakendaki te': {
      translation: "Je ne suis pas allé(e)",
      translationEn: "I did not go",
      explanation: "Négation au passé. Verbe au passé (-aki) + te en fin de phrase.",
      explanationEn: "Negation in the past tense. Past-tense verb (-aki) + te at the end of the sentence.",
      example: "Nakendaki na feti te. (Je ne suis pas allé à la fête.)",
      exampleEn: "Nakendaki na feti te. (I did not go to the party.)"
    },
    'Nakokende te': {
      translation: "Je n'irai pas",
      translationEn: "I will not go",
      explanation: "Négation au futur. Le futur (préfixe + ko + verbe) + te en fin de phrase.",
      explanationEn: "Negation in the future tense. The future (prefix + ko + verb) + te at the end of the sentence.",
      example: "Nakokende te soki ozali kolela. (Je n'irai pas si tu pleures.)",
      exampleEn: "Nakokende te soki ozali kolela. (I will not go if you cry.)"
    },
    'Azali te': {
      translation: "Il/Elle n'est pas là / Il n'y a pas",
      translationEn: "He/She is not here / There isn't",
      explanation: "Négation d'existence ou de présence. «Azali te» est l'une des expressions les plus courantes du quotidien.",
      explanationEn: "Negation of existence or presence. «Azali te» is one of the most common everyday expressions.",
      example: "Tata azali te. (Papa n'est pas là.)",
      exampleEn: "Tata azali te. (Dad is not here.)"
    },
    'Nayebi te': {
      translation: "Je ne sais pas",
      translationEn: "I don't know",
      explanation: "Négation du verbe «koyeba» (savoir). na- + yeba + te. Indispensable dans toute conversation !",
      explanationEn: "Negation of the verb «koyeba» (to know). na- + yeba + te. Essential in any conversation!",
      example: "Nayebi te wapi azali. (Je ne sais pas où il/elle est.)",
      exampleEn: "Nayebi te wapi azali. (I don't know where he/she is.)"
    },
    'Ezali te': {
      translation: "Il n'y a pas / Ça n'existe pas",
      translationEn: "There isn't any / It doesn't exist",
      explanation: "Négation impersonnelle d'existence. «E-» est le préfixe impersonnel pour les choses. «Ezali te» = c'est absent.",
      explanationEn: "Impersonal negation of existence. «E-» is the impersonal prefix for things. «Ezali te» = it is absent.",
      example: "Mbongo ezali te. (Il n'y a pas d'argent.)",
      exampleEn: "Mbongo ezali te. (There is no money.)"
    },
    'Elongi te': {
      translation: "Ce n'est pas convenable / Ça ne va pas",
      translationEn: "That's not proper / That's not right",
      explanation: "Expression figée de désapprobation. Vient de «kolonja» (convenir, aller bien).",
      explanationEn: "Fixed expression of disapproval. Comes from «kolonja» (to be fitting, to be right).",
      example: "Elongi te kosala bongo. (Ce n'est pas convenable d'agir ainsi.)",
      exampleEn: "Elongi te kosala bongo. (It is not proper to act that way.)"
    },
  },

  // ============================================================
  // UNITÉ 3 — VOCABULAIRE GRAMMATICAL
  // ============================================================

  'Prépositions de lieu': {
    'na': {
      translation: "À / Dans / En / Sur (position générale)",
      translationEn: "At / In / On (general position)",
      explanation: "Préposition polyvalente, la plus fréquente du lingala. Indique la localisation, la direction ou l'accompagnement selon le contexte.",
      explanationEn: "Versatile preposition, the most frequent in Lingala. Indicates location, direction, or accompaniment depending on context.",
      example: "Nazali na ndako. (Je suis à la maison.)",
      exampleEn: "Nazali na ndako. (I am at home.)",
      audio: "Li-na"
    },
    'kati na': {
      translation: "Dans / À l'intérieur de",
      translationEn: "In / Inside",
      explanation: "«Kati» = intérieur, milieu. «Kati na» précise une position à l'intérieur d'un espace fermé.",
      explanationEn: "«Kati» = interior, middle. «Kati na» specifies a position inside an enclosed space.",
      example: "Kati na ndako ezali molunge. (Il fait chaud à l'intérieur de la maison.)",
      exampleEn: "Kati na ndako ezali molunge. (It is hot inside the house.)",
      audio: "Li-kati_na"
    },
    'likolo ya': {
      translation: "Sur / Au-dessus de",
      translationEn: "On / Above",
      explanation: "«Likolo» = hauteur, dessus. «Likolo ya» indique une position supérieure.",
      explanationEn: "«Likolo» = height, top. «Likolo ya» indicates a position above.",
      example: "Kitabu ezali likolo ya mesa. (Le livre est sur la table.)",
      exampleEn: "Kitabu ezali likolo ya mesa. (The book is on the table.)",
      audio: "Li-likolo_ya"
    },
    'nse ya': {
      translation: "Sous / En dessous de",
      translationEn: "Under / Below",
      explanation: "«Nse» = bas, dessous. S'oppose à «likolo ya».",
      explanationEn: "«Nse» = bottom, below. Opposite of «likolo ya».",
      example: "Sapatu ezali nse ya kiti. (La chaussure est sous la chaise.)",
      exampleEn: "Sapatu ezali nse ya kiti. (The shoe is under the chair.)",
      audio: "Li-nse_ya"
    },
    'nsima ya': {
      translation: "Derrière",
      translationEn: "Behind",
      explanation: "«Nsima» = dos, arrière. «Nsima ya» indique une position postérieure dans l'espace.",
      explanationEn: "«Nsima» = back, rear. «Nsima ya» indicates a position behind in space.",
      example: "Azali nsima ya ndako. (Il/Elle est derrière la maison.)",
      exampleEn: "Azali nsima ya ndako. (He/She is behind the house.)",
      audio: "Li-nsima_ya"
    },
    'liboso ya': {
      translation: "Devant / En face de",
      translationEn: "In front of / Facing",
      explanation: "«Liboso» = avant, front. S'oppose à «nsima ya».",
      explanationEn: "«Liboso» = front, before. Opposite of «nsima ya».",
      example: "Azali liboso ya salle. (Il/Elle est devant la salle.)",
      exampleEn: "Azali liboso ya salle. (He/She is in front of the hall.)",
      audio: "Li-liboso_ya"
    },
    'pene ya': {
      translation: "Près de / À côté de",
      translationEn: "Near / Next to",
      explanation: "«Pene» = proximité. Indique une position géographique proche.",
      explanationEn: "«Pene» = proximity. Indicates a nearby geographical position.",
      example: "Ndako ezali pene ya nzela monene. (La maison est près de la grande route.)",
      exampleEn: "Ndako ezali pene ya nzela monene. (The house is near the main road.)",
      audio: "Li-pene_ya"
    },
    'mosika ya': {
      translation: "Loin de",
      translationEn: "Far from",
      explanation: "«Mosika» = distance, éloignement. S'oppose à «pene ya».",
      explanationEn: "«Mosika» = distance, remoteness. Opposite of «pene ya».",
      example: "Mboka ezali mosika ya awa. (Le village est loin d'ici.)",
      exampleEn: "Mboka ezali mosika ya awa. (The village is far from here.)"
    },
    'ntei ya': {
      translation: "Au milieu de / Entre",
      translationEn: "In the middle of / Between",
      explanation: "«Ntei» = centre, milieu. Indique une position centrale entre plusieurs éléments.",
      explanationEn: "«Ntei» = center, middle. Indicates a central position among several elements.",
      example: "Azali ntei ya bato. (Il/Elle est au milieu des gens.)",
      exampleEn: "Azali ntei ya bato. (He/She is in the middle of the people.)"
    },
  },

  'Prépositions de temps et de relation': {
    'sima ya': {
      translation: "Après (temps)",
      translationEn: "After (time)",
      explanation: "«Sima» employé dans un sens temporel. Indique une succession d'événements dans le temps.",
      explanationEn: "«Sima» used in a temporal sense. Indicates a succession of events in time.",
      example: "Nakoliya sima ya mosala. (Je mangerai après le travail.)",
      exampleEn: "Nakoliya sima ya mosala. (I will eat after work.)"
    },
    'liboso ya': {
      translation: "Avant (temps)",
      translationEn: "Before (time)",
      explanation: "«Liboso» employé dans un sens temporel — avant un événement ou une action à venir.",
      explanationEn: "«Liboso» used in a temporal sense — before an event or an upcoming action.",
      example: "Yekola liboso ya kolala. (Étudie avant de dormir.)",
      exampleEn: "Yekola liboso ya kolala. (Study before sleeping.)"
    },
    'banda': {
      translation: "Depuis / À partir de",
      translationEn: "Since / Starting from",
      explanation: "«Banda» indique un point de départ dans le temps.",
      explanationEn: "«Banda» indicates a starting point in time.",
      example: "Nazali awa banda ntongo. (Je suis ici depuis le matin.)",
      exampleEn: "Nazali awa banda ntongo. (I have been here since morning.)"
    },
    'mpaka': {
      translation: "Jusqu'à / Au point que",
      translationEn: "Until / To the point that",
      explanation: "«Mpaka» indique une limite temporelle ou spatiale. Peut aussi exprimer l'intensité : «mpaka» = tellement que.",
      explanationEn: "«Mpaka» indicates a temporal or spatial limit. Can also express intensity: «mpaka» = so much that.",
      example: "Nasalaki mpaka butu. (J'ai travaillé jusqu'à la nuit.)",
      exampleEn: "Nasalaki mpaka butu. (I worked until nightfall.)",
      audio: "Li-mpaka"
    },
    'mpo na': {
      translation: "Pour / À cause de",
      translationEn: "For / Because of",
      explanation: "«Mpo» exprime le but ou la cause. «Mpo na» + nom = à cause de / pour quelque chose.",
      explanationEn: "«Mpo» expresses purpose or cause. «Mpo na» + noun = because of / for something.",
      example: "Nasalaki mpo na bana na ngai. (J'ai travaillé pour mes enfants.)",
      exampleEn: "Nasalaki mpo na bana na ngai. (I worked for my children.)",
      audio: "Li-mpo_na"
    },
    'elongo na': {
      translation: "Avec / Ensemble avec",
      translationEn: "With / Together with",
      explanation: "«Elongo» = ensemble. «Elongo na» + personne = accompagnement.",
      explanationEn: "«Elongo» = together. «Elongo na» + person = accompaniment.",
      example: "Nakendaki elongo na mama. (Je suis allé avec maman.)",
      exampleEn: "Nakendaki elongo na mama. (I went with mom.)",
      audio: "Li-elongo_na"
    },
    'tango': {
      translation: "Quand / Lorsque (subordonnée)",
      translationEn: "When (subordinate clause)",
      explanation: "«Tango» introduit une subordonnée temporelle. À distinguer de «tango nini» (question : quand?).",
      explanationEn: "«Tango» introduces a temporal subordinate clause. To be distinguished from «tango nini» (question: when?).",
      example: "Tango nakomi, bazalaki kolya. (Quand je suis arrivé, ils étaient en train de manger.)",
      exampleEn: "Tango nakomi, bazalaki kolya. (When I arrived, they were eating.)"
    },
  },

  'Adjectifs — Apparence et taille': {
    'monene': {
      translation: "Grand / Important",
      translationEn: "Big / Important",
      explanation: "Adjectif de taille et d'importance. Très polyvalent : peut qualifier une personne, un objet ou une institution.",
      explanationEn: "Adjective of size and importance. Very versatile: can describe a person, an object, or an institution.",
      example: "Ndako monene ezali na ntei ya mboka. (La grande maison est au centre du village.)",
      exampleEn: "Ndako monene ezali na ntei ya mboka. (The big house is in the center of the village.)",
      audio: "Li-monene"
    },
    'moke': {
      translation: "Petit",
      translationEn: "Small",
      explanation: "Adjectif de petite taille. Suit directement le nom qualifié.",
      explanationEn: "Adjective for small size. Directly follows the noun it qualifies.",
      example: "Mwana moke azali kosana. (Le petit enfant joue.)",
      exampleEn: "Mwana moke azali kosana. (The small child is playing.)",
      audio: "Li-moke"
    },
    'molai': {
      translation: "Long",
      translationEn: "Long / Tall",
      explanation: "Adjectif de longueur ou de durée.",
      explanationEn: "Adjective of length or duration.",
      example: "Nzela molai ezali mpasi. (Le long chemin est difficile.)",
      exampleEn: "Nzela molai ezali mpasi. (The long road is difficult.)",
      audio: "Li-molai"
    },
    'mokuse': {
      translation: "Court",
      translationEn: "Short",
      explanation: "S'oppose à «molai». Qualifie aussi bien une durée qu'une longueur physique.",
      explanationEn: "Opposite of «molai». Describes both duration and physical length.",
      example: "Nzela mokuse ezali malamu. (Le chemin court est bien.)",
      exampleEn: "Nzela mokuse ezali malamu. (The short road is good.)"
    },
    'kitoko': {
      translation: "Beau / Joli",
      translationEn: "Beautiful / Pretty",
      explanation: "Adjectif esthétique très courant en lingala. Peut qualifier personnes et objets.",
      explanationEn: "Very common aesthetic adjective in Lingala. Can describe people and objects.",
      example: "Mobali kitoko. (Bel homme.) — Mwasi kitoko. (Belle femme.)",
      exampleEn: "Mobali kitoko. (Handsome man.) — Mwasi kitoko. (Beautiful woman.)",
      audio: "Li-kitoko"
    },
    'moto': {
      translation: "Chaud",
      translationEn: "Hot",
      explanation: "Adjectif de température. Attention : «moto» signifie aussi «personne» — le contexte différencie les deux sens.",
      explanationEn: "Adjective of temperature. Note: «moto» also means «person» — context distinguishes the two meanings.",
      example: "Mai moto. (Eau chaude.)",
      exampleEn: "Mai moto. (Hot water.)"
    },
    'bilili': {
      translation: "Froid",
      translationEn: "Cold",
      explanation: "Adjectif de température froide. S'oppose à «moto» dans le sens thermique.",
      explanationEn: "Adjective for cold temperature. Opposite of «moto» in the thermal sense.",
      example: "Mopepe ya bilili. (Vent froid.)",
      exampleEn: "Mopepe ya bilili. (Cold wind.)",
      audio: "Li-bilili"
    },
    'petɛpetɛ': {
      translation: "Étroit / Serré",
      translationEn: "Narrow / Tight",
      explanation: "Adjectif de dimension horizontale réduite. Utilisé pour les chemins, espaces ou vêtements.",
      explanationEn: "Adjective for reduced horizontal dimension. Used for paths, spaces, or clothing.",
      example: "Nzela petɛpetɛ. (Chemin étroit.)",
      exampleEn: "Nzela petɛpetɛ. (Narrow path.)",
      audio: "Li-petepete"
    },
  },

  'Adjectifs — Qualité et état': {
    'malamu': {
      translation: "Bon / Bien / Correct",
      translationEn: "Good / Well / Correct",
      explanation: "L'un des adjectifs les plus fréquents du lingala. Qualifie aussi bien une qualité morale, physique, gustative ou un état de santé.",
      explanationEn: "One of the most frequent adjectives in Lingala. Describes moral, physical, or taste-related qualities as well as a state of health.",
      example: "Bilei malamu. (Bonne nourriture.) — Ozali malamu? (Tu vas bien?)",
      exampleEn: "Bilei malamu. (Good food.) — Ozali malamu? (Are you well?)",
      audio: "Li-malamu"
    },
    'mabe': {
      translation: "Mauvais / Mal",
      translationEn: "Bad / Poorly",
      explanation: "Antonyme de «malamu». Qualifie une mauvaise qualité, une mauvaise action ou un mauvais état.",
      explanationEn: "Antonym of «malamu». Describes a poor quality, a bad action, or a bad state.",
      example: "Kosala mabe elongi te. (Faire le mal n'est pas convenable.)",
      exampleEn: "Kosala mabe elongi te. (Doing evil is not proper.)",
      audio: "Li-mabe"
    },
    'pɛto': {
      translation: "Propre / Pur",
      translationEn: "Clean / Pure",
      explanation: "Adjectif de propreté physique ou morale.",
      explanationEn: "Adjective of physical or moral cleanliness.",
      example: "Ndako pɛto. (Maison propre.)",
      exampleEn: "Ndako pɛto. (Clean house.)",
      audio: "Li-peto"
    },
    'mbindo': {
      translation: "Sale / Impur",
      translationEn: "Dirty / Impure",
      explanation: "Antonyme de «pɛto». Peut qualifier un objet, un lieu ou un comportement.",
      explanationEn: "Antonym of «pɛto». Can describe an object, a place, or a behavior.",
      example: "Bilamba mbindo. (Vêtements sales.)",
      exampleEn: "Bilamba mbindo. (Dirty clothes.)",
      audio: "Li-mbindo"
    },
    'makasi': {
      translation: "Fort / Puissant / Intense",
      translationEn: "Strong / Powerful / Intense",
      explanation: "Adjectif de force, d'intensité ou de difficulté. Très expressif, souvent utilisé pour amplifier.",
      explanationEn: "Adjective of strength, intensity, or difficulty. Very expressive, often used to amplify.",
      example: "Mobali makasi. (Homme fort.) — Mpasi makasi. (Grande douleur.)",
      exampleEn: "Mobali makasi. (Strong man.) — Mpasi makasi. (Great pain.)",
      audio: "Li-makasi"
    },
    'mɔlu': {
      translation: "Mou / Doux / Tendre",
      translationEn: "Soft / Gentle / Tender",
      explanation: "Adjectif de texture douce ou molle. S'oppose à «mpimpa».",
      explanationEn: "Adjective for soft texture. Opposite of «mpimpa».",
      example: "Bilei mɔlu. (Nourriture tendre.)",
      exampleEn: "Bilei mɔlu. (Tender food.)",
      audio: "Li-molu"
    },
    'mpimpa': {
      translation: "Dur / Solide",
      translationEn: "Hard / Solid",
      explanation: "Adjectif de dureté et de solidité. S'oppose à «mɔlu».",
      explanationEn: "Adjective for hardness and solidity. Opposite of «mɔlu».",
      example: "Libanga mpimpa. (Pierre dure.)",
      exampleEn: "Libanga mpimpa. (Hard stone.)",
      audio: "Li-mpimpa"
    },
    'mpasi': {
      translation: "Difficile / Douloureux",
      translationEn: "Difficult / Painful",
      explanation: "Adjectif de difficulté ou de douleur physique/morale. Très courant.",
      explanationEn: "Adjective of difficulty or physical/emotional pain. Very common.",
      example: "Mosala mpasi. (Travail difficile.)",
      exampleEn: "Mosala mpasi. (Difficult work.)",
      audio: "Li-mpasi"
    },
  },

  'Adjectifs — Caractère': {
    'mayele': {
      translation: "Intelligent / Habile",
      translationEn: "Intelligent / Clever",
      explanation: "Adjectif qualifiant l'intelligence ou l'habileté. «Moto na mayele» = une personne intelligente/habile.",
      explanationEn: "Adjective describing intelligence or skill. «Moto na mayele» = an intelligent/clever person.",
      example: "Mwana na mayele akoli malamu. (L'enfant intelligent grandira bien.)",
      exampleEn: "Mwana na mayele akoli malamu. (The intelligent child will grow up well.)",
      audio: "Li-mayele"
    },
    'mbangu': {
      translation: "Rapide / Vif",
      translationEn: "Fast / Quick",
      explanation: "Adjectif de vitesse. Peut qualifier une personne, un mouvement ou une réaction.",
      explanationEn: "Adjective of speed. Can describe a person, a movement, or a reaction.",
      example: "Moto ya mbangu. (Personne rapide.)",
      exampleEn: "Moto ya mbangu. (Fast person.)",
      audio: "Li-mbangu"
    },
    'ya sika': {
      translation: "Nouveau / Récent",
      translationEn: "New / Recent",
      explanation: "«Sika» = neuf, nouveau. «Ya sika» se place après le nom comme tous les adjectifs en lingala.",
      explanationEn: "«Sika» = new, brand new. «Ya sika» is placed after the noun, like all adjectives in Lingala.",
      example: "Bilamba ya sika. (Vêtements neufs.)",
      exampleEn: "Bilamba ya sika. (New clothes.)",
      audio: "Li-ya_sika"
    },
    'ya kala': {
      translation: "Vieux / Ancien",
      translationEn: "Old / Former",
      explanation: "«Kala» = passé, autrefois. «Ya kala» indique l'ancienneté ou ce qui vient du passé.",
      explanationEn: "«Kala» = the past, formerly. «Ya kala» indicates age or something from the past.",
      example: "Tango ya kala. (Les temps anciens.)",
      exampleEn: "Tango ya kala. (The old times.)",
      audio: "Li-ya_kala"
    },
    'alɛmbi': {
      translation: "Fatigué",
      translationEn: "Tired",
      explanation: "Forme participiale passive de «kolɛmba» (se fatiguer). Se construit comme un adjectif épithète.",
      explanationEn: "Passive participial form of «kolɛmba» (to get tired). Used as an attributive adjective.",
      example: "Moto alɛmbi. (Personne fatiguée.) — Nazali kolɛmba. (Je suis en train de me fatiguer.)",
      exampleEn: "Moto alɛmbi. (Tired person.) — Nazali kolɛmba. (I am getting tired.)",
      audio: "Li-alembi"
    },
    'liboso': {
      translation: "Premier / Principal",
      translationEn: "First / Main",
      explanation: "«Liboso» = devant, avant. Utilisé comme adjectif ordinal de rang.",
      explanationEn: "«Liboso» = in front, before. Used as an ordinal adjective of rank.",
      example: "Mokambi ya liboso. (Le premier chef / Premier responsable.)",
      exampleEn: "Mokambi ya liboso. (The first chief / Main leader.)"
    },
    'ya suka': {
      translation: "Dernier",
      translationEn: "Last",
      explanation: "«Suka» = fin, bout. «Ya suka» qualifie ce qui est en dernière position.",
      explanationEn: "«Suka» = end, tip. «Ya suka» describes what is in the last position.",
      example: "Mwana ya suka. (Le dernier enfant / le benjamin.)",
      exampleEn: "Mwana ya suka. (The last child / the youngest.)",
      audio: "Li-ya_suka"
    },
  },

  'Conjonctions': {
    'pe / mpe': {
      translation: "Et / Aussi / De plus",
      translationEn: "And / Also / Moreover",
      explanation: "Conjonction d'addition. «Pe» est la forme courante, «mpe» est plus soutenue. Relie des noms ou des phrases.",
      explanationEn: "Additive conjunction. «Pe» is the common form, «mpe» is more formal. Links nouns or sentences.",
      example: "Naliyaki mpe nalailaki. (J'ai mangé et j'ai dormi.)",
      exampleEn: "Naliyaki mpe nalailaki. (I ate and I slept.)"
    },
    'kasi': {
      translation: "Mais",
      translationEn: "But",
      explanation: "Conjonction d'opposition ou de restriction. Placée en début de la deuxième proposition.",
      explanationEn: "Conjunction of opposition or restriction. Placed at the start of the second clause.",
      example: "Nazali koliya, kasi bilei ezali ya mabe. (Je mange, mais la nourriture est mauvaise.)",
      exampleEn: "Nazali koliya, kasi bilei ezali ya mabe. (I am eating, but the food is bad.)"
    },
    'to': {
      translation: "Ou",
      translationEn: "Or",
      explanation: "Conjonction de disjonction ou d'alternative. Relie deux options.",
      explanationEn: "Conjunction of disjunction or alternative. Links two options.",
      example: "Olingi bilei to mai? (Tu veux de la nourriture ou de l'eau?)",
      exampleEn: "Olingi bilei to mai? (Do you want food or water?)"
    },
    'mpo': {
      translation: "Parce que / Pour que",
      translationEn: "Because / So that",
      explanation: "Conjonction de cause ou de but. «Mpo» introduit une subordonnée causale ou finale.",
      explanationEn: "Conjunction of cause or purpose. «Mpo» introduces a causal or purpose clause.",
      example: "Nasalaka mpo nakolya. (Je travaille pour pouvoir manger.)",
      exampleEn: "Nasalaka mpo nakolya. (I work so that I can eat.)"
    },
    'soki': {
      translation: "Si (condition)",
      translationEn: "If (condition)",
      explanation: "Conjonction conditionnelle. Introduit une hypothèse. La conséquence suit dans la proposition principale.",
      explanationEn: "Conditional conjunction. Introduces a hypothesis. The consequence follows in the main clause.",
      example: "Soki olingi, yaka. (Si tu veux, viens.)",
      exampleEn: "Soki olingi, yaka. (If you want, come.)"
    },
    'tango': {
      translation: "Quand / Lorsque",
      translationEn: "When",
      explanation: "Conjonction temporelle. À distinguer de son emploi comme mot interrogatif («tango nini» = quand?).",
      explanationEn: "Temporal conjunction. To be distinguished from its use as an interrogative word («tango nini» = when?).",
      example: "Tango nakomi, bazalaki kolya. (Quand je suis arrivé, ils mangeaient.)",
      exampleEn: "Tango nakomi, bazalaki kolya. (When I arrived, they were eating.)"
    },
    'nzokande': {
      translation: "Cependant / Pourtant",
      translationEn: "However / Yet",
      explanation: "Conjonction adversative exprimant une concession ou un contraste inattendu.",
      explanationEn: "Adversative conjunction expressing a concession or an unexpected contrast.",
      example: "Asalaki makasi, nzokande akoki te. (Il a travaillé dur, pourtant il n'a pas réussi.)",
      exampleEn: "Asalaki makasi, nzokande akoki te. (He worked hard, yet he did not succeed.)"
    },
    'atako': {
      translation: "Bien que / Même si",
      translationEn: "Although / Even though",
      explanation: "Conjonction concessive. Exprime une opposition entre deux faits vrais simultanément.",
      explanationEn: "Concessive conjunction. Expresses an opposition between two facts that are simultaneously true.",
      example: "Atako azali moke, azali makasi. (Bien qu'il soit petit, il est fort.)",
      exampleEn: "Atako azali moke, azali makasi. (Although he is small, he is strong.)"
    },
  },

  'Adverbes': {
    'sikoyo': {
      translation: "Maintenant / Actuellement",
      translationEn: "Now / Currently",
      explanation: "Adverbe de temps très courant. Exprime le moment présent ou la période actuelle.",
      explanationEn: "Very common adverb of time. Expresses the present moment or current period.",
      example: "Sikoyo nayebi koloba lingala. (Maintenant je sais parler le lingala.)",
      exampleEn: "Sikoyo nayebi koloba lingala. (Now I know how to speak Lingala.)"
    },
    'lelo': {
      translation: "Aujourd'hui",
      translationEn: "Today",
      explanation: "Adverbe de temps pour la journée en cours.",
      explanationEn: "Adverb of time for the current day.",
      example: "Lelo ezali mokolo ya malamu. (Aujourd'hui c'est un bon jour.)",
      exampleEn: "Lelo ezali mokolo ya malamu. (Today is a good day.)",
      audio: "Li-lelo"
    },
    'lobi': {
      translation: "Demain",
      translationEn: "Tomorrow",
      explanation: "Adverbe de temps pour le jour suivant. «Lobi ya liboso» peut aussi désigner le lendemain dans un récit.",
      explanationEn: "Adverb of time for the following day. «Lobi ya liboso» can also refer to the next day within a narrative.",
      example: "Nakokende lobi. (J'irai demain.)",
      exampleEn: "Nakokende lobi. (I will go tomorrow.)",
      audio: "Li-lobi"
    },
    'yambo': {
      translation: "D'abord / Premièrement",
      translationEn: "First / Firstly",
      explanation: "Adverbe d'ordre ou de priorité. Indique une action à effectuer en priorité.",
      explanationEn: "Adverb of order or priority. Indicates an action to be done first.",
      example: "Yekola yambo, sima osala. (Étudie d'abord, ensuite tu travailles.)",
      exampleEn: "Yekola yambo, sima osala. (Study first, then you work.)"
    },
    'mingi': {
      translation: "Beaucoup / Très",
      translationEn: "A lot / Very",
      explanation: "Adverbe de quantité et d'intensité. L'un des plus fréquents du lingala — peut qualifier un verbe ou amplifier un adjectif.",
      explanationEn: "Adverb of quantity and intensity. One of the most frequent in Lingala — can qualify a verb or amplify an adjective.",
      example: "Nalingi yo mingi. (Je t'aime beaucoup.)",
      exampleEn: "Nalingi yo mingi. (I love you a lot.)"
    },
    'seko': {
      translation: "Jamais",
      translationEn: "Never",
      explanation: "Adverbe de fréquence négatif absolu. S'utilise avec ou sans «te» selon le registre.",
      explanationEn: "Adverb of absolute negative frequency. Used with or without «te» depending on register.",
      example: "Nasalaki bongo seko te. (Je n'ai jamais fait ça.)",
      exampleEn: "Nasalaki bongo seko te. (I have never done that.)",
      audio: "Li-seko"
    },
    'naino': {
      translation: "Encore / Toujours / Pas encore",
      translationEn: "Still / Yet / Not yet",
      explanation: "Adverbe polyvalent : «naino» seul = encore/toujours ; «naino...te» = pas encore.",
      explanationEn: "Versatile adverb: «naino» alone = still/yet; «naino...te» = not yet.",
      example: "Azali naino awa. (Il est encore là.) — Akomi te naino. (Il n'est pas encore arrivé.)",
      exampleEn: "Azali naino awa. (He is still here.) — Akomi te naino. (He has not yet arrived.)"
    },
    'tango nyonso': {
      translation: "Toujours / À chaque fois",
      translationEn: "Always / Every time",
      explanation: "«Tango nyonso» = tout le temps, chaque fois. Exprime la régularité absolue.",
      explanationEn: "«Tango nyonso» = all the time, every time. Expresses absolute regularity.",
      example: "Nazalaka na esengo tango nyonso. (Je suis toujours heureux.)",
      exampleEn: "Nazalaka na esengo tango nyonso. (I am always happy.)"
    },
  },

};

export default grammaire;
